import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Tag } from '@fiverr-private/fit';
import { Button } from '@fiverr-private/button';
import { Rating } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import { buildGigPageUrl, getCategoryKey } from '../../utils';
import { convertFromUSD } from '../../../utils/currencyConverter';
import { ENGINE_TYPE } from '../../../shared/constants';
import { clickOnGigServiceCardEvent } from '../../../utils/biEvents/events';
import { AssetsCarousel } from '../../AssetsCarousel';
import { transformToCamelCase } from '../../../utils';
import { AssetThumbnailImage } from '../../AssetThumbnail/AssetThumbnailImage';
import {
    clickOnGigServiceCardImageEvent,
    hoverOnGigServiceCardImageEvent,
    scrollOnGigServiceCardImageEvent,
} from '../../../utils/biEvents/events/gigCardImages';
import { currencyType } from '../../../shared/types';
import { GigServiceCardLightbox } from '../GigServiceCardLightbox';
import { useGigServiceCardLightbox } from '../GigServiceCardLightbox/useGigServiceCardLightbox';
import { TitleEllipsisPopover } from './TitleEllipsisPopover';
import { getImagePreview } from './utils';
import { biEnrichmentType, gigType } from './propTypes';

import './index.scss';

const T_PREFIX = 'listings.gig_service_card';
const MAX_COUNT = 1000;

/** @param {number} count */
const getCountText = (count) => (count > MAX_COUNT ? translate(`${T_PREFIX}.count_format`) : count);

const fireClickBIpayload = ({ source, gig, biEnrichment }) =>
    clickOnGigServiceCardEvent({
        source,
        gigId: gig.gig_id,
        sellerUsername: gig.seller_name,
        sellerId: gig.seller_id,
        componentName: 'gig_card',
        ...biEnrichment,
    });

export const GigServiceCard = ({
    className,
    fiverrChoice,
    isVertical,
    isRelevant,
    isBig,
    gig,
    showTranslatedUGC,
    onClick = noop,
    openInNewTab = false,
    position,
    currency,
    source,
    biEnrichment,
}) => {
    const {
        category_id: categoryId,
        sub_category_id: subCategoryId,
        nested_sub_category_id: nestedSubCategoryId,
        package_i: packageId,
        cached_slug: slug,
        seller_name: username,
        assets,
        attachments = [],
        filtered_delivery_attachments: filteredDeliveryAttachments,
        buying_review_rating,
        buying_review_rating_count,
        studio,
        agency,
    } = gig;

    const {
        locale,
        isMobile,
        userId,
        experience: { isSeller },
    } = getContext();

    const { isPreviewOpened, sourceInitialIndex, setSourceInitialIndex, sources, closePreview, openPreview } =
        useGigServiceCardLightbox({ assets, attachments, filteredDeliveryAttachments });

    const assetImageUrl = getImagePreview(assets, attachments);

    const price = convertFromUSD(gig.price_i, currency);

    const gigLinkTarget = openInNewTab ? '_blank' : '_self';
    const gigLinkUrl = useMemo(
        () =>
            buildGigPageUrl({
                username,
                slug,
                packageId,
                source,
                engine: fiverrChoice ? ENGINE_TYPE.FIVERR_CHOICE : undefined,
            }),
        [packageId, slug, username, source, fiverrChoice]
    );

    const showLinkButton = !isVertical;
    const isBigGridCell = isBig && !isVertical;

    const isStudio = !!studio;
    const isAgency = !!agency && agency.status?.toUpperCase() === 'ACTIVE' && agency.slug;
    const gigTitleSuffix = (isAgency && 'our_agency_will') || (isStudio && 'our_studio_will') || 'i_will';
    const gigTitleKey = `listings.gig_cards.${gigTitleSuffix}`;
    const gigTitle =
        (showTranslatedUGC && gig.translatedTitle) || translate(gigTitleKey, { locale, params: { title: gig.title } });

    const onCardClick = useCallback(
        (e) => {
            if (e?.target?.href) {
                return;
            }
            onClick();
            fireClickBIpayload({ source, gig, biEnrichment });
            window.open(gigLinkUrl, gigLinkTarget);
        },
        [gigLinkUrl, gigLinkTarget, source, gig, onClick, biEnrichment]
    );

    const onLinkClick = useCallback(() => {
        onClick();
        fireClickBIpayload({ source, gig, biEnrichment });
    }, [source, gig, onClick, biEnrichment]);

    const classNames = classnames(className, 'gig-service-card', 'box-shadow-z1', {
        'vertical-tiny': isVertical,
        'is-big-cell': isBigGridCell,
    });

    const carouselItemImageClickHandle = () => {
        openPreview();
        clickOnGigServiceCardImageEvent({
            gigId: gig.gig_id,
            target: 'click',
            sourceStr: 'image',
            position,
            userId,
            isSeller,
            sellerId: gig.seller_id,
            source,
        });
    };

    const carouselItemImageHoverHandle = () => {
        hoverOnGigServiceCardImageEvent({
            gigId: gig.gig_id,
            target: 'click',
            sourceStr: 'image',
            position,
            userId,
            isSeller,
            sellerId: gig.seller_id,
            source,
        });
    };

    return (
        <>
            <div className={classNames} onClick={onCardClick} data-is-pro={gig.is_pro} data-gig-id={gig.gig_id}>
                <div className="top-wrapper flex">
                    <div className="assets-wrapper m-r-20">
                        {isBigGridCell ? (
                            <AssetsCarousel
                                className="gig-assets-carousel"
                                onScroll={({ currentPosition }) => {
                                    setSourceInitialIndex(currentPosition);
                                    scrollOnGigServiceCardImageEvent({
                                        gigId: gig.gig_id,
                                        target: 'scroll',
                                        sourceStr: 'carousel',
                                        position: currentPosition,
                                        userId,
                                        isSeller,
                                        sellerId: gig.seller_id,
                                        source,
                                    });
                                }}
                                gigIds={{ id: gig.gig_id, categoryId, subCategoryId, nestedSubCategoryId }}
                                assets={transformToCamelCase(assets)}
                                attachments={transformToCamelCase(attachments)}
                                filteredDeliveryAttachments={filteredDeliveryAttachments}
                                onAssetClick={openPreview}
                                source={source}
                            />
                        ) : (
                            <AssetThumbnailImage
                                src={assetImageUrl}
                                onClick={carouselItemImageClickHandle}
                                onHover={carouselItemImageHoverHandle}
                            />
                        )}
                    </div>
                    <div className={classnames('content-wrapper', { 'is-relevant': isRelevant })}>
                        <div className="title-wrapper">
                            {isRelevant && (
                                <Tag
                                    className="badge-relevant"
                                    size={Tag.SIZES.TINY}
                                    color={Tag.COLORS.BUSINESS_400}
                                    borderRadius={Tag.BORDER_RADIUSES.ROUNDED}
                                >
                                    <I18n k={`${T_PREFIX}.most_relevant`} />
                                </Tag>
                            )}
                            <TitleEllipsisPopover
                                className="category-title text-semi-bold co-text-darkest"
                                text={<I18n k={getCategoryKey({ categoryId, subCategoryId, nestedSubCategoryId })} />}
                            />
                        </div>
                        <TitleEllipsisPopover
                            className="description m-t-4 m-b-4 tbody-5 co-text-medium"
                            text={gigTitle}
                        />
                        <Rating
                            className="rating"
                            scoreClassName="score"
                            ratingsCountClassName="counts"
                            score={buying_review_rating}
                            ratingsCount={
                                buying_review_rating_count ? getCountText(buying_review_rating_count) : undefined
                            }
                            color={Rating.COLORS.BLACK}
                            scoreMinimumFractionDigits={1}
                            scoreMaximumFractionDigits={1}
                        />
                    </div>
                </div>
                <div className={classnames('footer-wrapper flex flex-justify-between flex-items-end')}>
                    <span className="flex flex-col">
                        <I18n k={`${T_PREFIX}.from`} />
                        <span className="price text-semi-bold">
                            <I18n
                                k={`${T_PREFIX}.price`}
                                params={{ price }}
                                templates={{
                                    tBody: (text) => <span className="tbody-4">{text}</span>,
                                }}
                            />
                        </span>
                    </span>
                    {showLinkButton && (
                        <Button
                            className="details-button"
                            href={gigLinkUrl}
                            target={gigLinkTarget}
                            size="sm"
                            variant="outline"
                            intent="secondary"
                            onClick={onLinkClick}
                        >
                            <I18n k={`${T_PREFIX}.more_details`} />
                        </Button>
                    )}
                </div>
            </div>
            {!isMobile && isPreviewOpened && (
                <GigServiceCardLightbox
                    sources={sources}
                    sourceInitialIndex={sourceInitialIndex}
                    onScroll={() => {
                        scrollOnGigServiceCardImageEvent({
                            gigId: gig.gig_id,
                            target: 'scroll',
                            sourceStr: 'Lightbox carousel',
                            userId,
                            isSeller,
                            sellerId: gig.seller_id,
                            source,
                        });
                    }}
                    onClose={closePreview}
                />
            )}
        </>
    );
};

GigServiceCard.propTypes = {
    biEnrichment: biEnrichmentType,
    position: PropTypes.number,
    className: PropTypes.string,
    gig: gigType,
    fiverrChoice: PropTypes.bool,
    isVertical: PropTypes.bool,
    isRelevant: PropTypes.bool,
    isBig: PropTypes.bool,
    showTranslatedUGC: PropTypes.bool,
    onClick: PropTypes.func,
    openInNewTab: PropTypes.bool,
    currency: currencyType,
    source: PropTypes.string,
};
